import { Metamodel } from 'app/model/base/model';
import { Collection, IViolation } from '../base/annotation';
import { SingleStreamOutputTransformation } from './single-output-stream.transformation';
import { DataFlowMapColumn } from './dataflow-map.column';

import { sanitizeAndFormatString } from 'app/shared/utils/string.util';
import { DataFlowColumn, IBasedOnEntityRef } from '../dataset/dataflowcolumn';
import { IExtraFieldInfo } from 'app/interfaces/IExtraFieldInfo';
import { DataFlowExpressionBasedMapColumn } from './dataflow-expression-based-map.column';
import { Owned } from '../base/named';
import { IMapColumn } from './IMapColumn';
import { ExpressionBasedColumnBase } from './expression-based.column.base';
import { IValidationContext, stringIsNullOrEmpty, UniqueCaseSensitiveValidator } from 'shell-models-lib';
import { ColumnExpressionProcessor } from './column-expression-processor';
import { PatternExpressionColumnsUtils } from 'app/authoring/component/transformations/pattern-expression-columns-utils';
import { IDataFlowArguments, generateGrammarForColumns, getSortedExpressionsInColumnArguments } from './dataflow-expression-utilities';
import { ColumnReference } from '../dataset/columnReference';
import { Observable, Subject } from 'rxjs';
import { Transformation } from './transformation';
import { BooleanDataFlowType, IDataFlowDataType } from './dataflow-datatype';
import { DataFlowExpression } from './dataflowexpression';
import { asyncUpdateIdentifiers, updateIdentifiers } from './dataflow-expression-update-identifiers';
import { ColumnMappingTransformationUtils } from './column-mapping-transformation-utilities';
import { ComplexTypeUtils } from 'app/authoring/component/transformations/complex-type-utils';
import { GlobalVariables } from 'app/services/globals';
import { CallTransformation } from './calltransformation';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { isSinkTypeEq } from 'app/model/dataflow/dataflow-utils';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { IResourceContext } from 'app/multi-contexts/resource-context.typings';

export interface IMappingResult {
    baseEntity: IBasedOnEntityRef;
    mapping: IMapColumn;
    outputColumn: ColumnReference;
}

@Metamodel.model({
    name: 'basecolumnmappingtransformation', // As this is abstract this will never be used
    abstract: true,
    constraints: {
        outputColumns: {
            validator: BaseColumnMappingTransformation.validateOutputColumnNames
        }
    }
})
export abstract class BaseColumnMappingTransformation extends SingleStreamOutputTransformation {

    @Metamodel.attribute({
        constraint: {
            validator: BaseColumnMappingTransformation.validateSkipDuplicateMapInputs
        }
    })
    public skipDuplicateMapInputs: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');

    @Metamodel.attribute({
        constraint: {
            validator: BaseColumnMappingTransformation.validateSkipDuplicateMapOutputs
        }
    })
    public skipDuplicateMapOutputs: DataFlowExpression = this.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');

    /* eslint-disable @typescript-eslint/member-ordering */
    private _updateMappings = new Subject<void>();
    public updateMappings$: Observable<void> = this._updateMappings.asObservable();

    @Metamodel.attribute({
        collection: Collection.Array,
        constraint: {
            validator: BaseColumnMappingTransformation.validateInputColumnExists
        },
        display: stringResources.Schema
    })
    public _schema: IMapColumn[] = [];

    public get schema(): IMapColumn[] {
        return this._schema;
    }

    public set schema(_schema: IMapColumn[]) {
        this._schema = _schema;
    }

    public get columns(): DataFlowColumn[] {
        if (this._currentMappingResults.length === 0) {
            this._updateOutputColumns();
        }
        return this._currentMappingResults.map((mappingResult) => mappingResult.outputColumn.column);
    }

    public get mappingResults(): IMappingResult[] {
        return this._currentMappingResults;
    }

    public get inputColumnsOnReset(): ColumnReference[] {
        return this.distinctInputColumnRefs;
    }

    public inspectSummaryOutputFields: IExtraFieldInfo[] = [
        {
            name: () => stringResources.TransformationInspect_Update,
            icon: () => 'svg-i-9x9-Update',
            value: this._getUpdatedColumnLength.bind(this)
        },
        {
            name: () => stringResources.TransformationInspect_Dropped,
            value: this._getDroppedColumnLength.bind(this)
        },
        {
            name: () => stringResources.TransformationInspect_Unchanged,
            value: this._getUnchangedColumnLength.bind(this)
        }
    ];

    public inspectDetailsOutputFields: IExtraFieldInfo[] = [
        {
            name: () => stringResources.TransformationInspect_Update,
            icon: this._getColumnUpdateIcon.bind(this)
        },
        {
            name: () => stringResources.TransformationInspect_Column_InputColumns,
            value: this._getInputColumnName.bind(this)
        }
    ];

    public showInspectInput = true;
    public populateMappingsOnEmptySchema: boolean = true;

    @Metamodel.attribute({
        transient: true
    })
    protected _currentMappingResults: IMappingResult[] = [];

    private _renamedColumns: DataFlowColumn[] = [];
    private _unusedInputColumns: ColumnReference[] = [];

    public static validateInputColumnExists(transformation: BaseColumnMappingTransformation): IViolation[] | undefined {
        const violations: IViolation[] = [];
        if (transformation.owner instanceof CallTransformation) {
            // call tx has nested transforms whose violations we will ignore
            return violations;
        }
        transformation.schema.filter((mapping) => mapping instanceof DataFlowMapColumn).forEach((mapping: DataFlowMapColumn) => {
            const columnRef = mapping.columnRef;
            // checking for name in case of exact object mismatch because there's a scenario when previewing data where
            // the dataflow object is cloned, but no fully deep so some properties (like column) are recreated causing column mismatch on the validation during preview
            const inputColumnExists = columnRef && transformation.distinctInputColumnRefs.some((inputColumnRef) => inputColumnRef.column === mapping.columnRef.column
                || inputColumnRef.column.name === mapping.columnRef.column.name);
            if (!inputColumnExists) {
                violations.push({
                    context: mapping,
                    message: () => columnRef ? sanitizeAndFormatString(stringResources.DataFlowSchemaEmptyColumnErrorWithName, columnRef.name) : stringResources.DataFlowSchemaEmptyColumnError
                });
            }
        });
        if (transformation instanceof DataFlowSink && isSinkTypeEq(adfModelTypeMap.dynamics.linkedServiceType, adfModelTypeMap.dynamics.datasetType)(transformation)) {
            const fieldNames: Set<string> = new Set<string>();
            const alternateKeyNames: Set<string> = new Set<string>();
            transformation.schema.forEach((mapping) => {
                if (mapping instanceof DataFlowMapColumn) {
                    const [fieldName, alternateKeyName] = mapping.outputAlias?.split('@');
                    if (!stringIsNullOrEmpty(alternateKeyName) && !mapping.outputAlias.startsWith('@')) {
                        if (fieldNames.has(fieldName)) {
                            if (!alternateKeyNames.has(alternateKeyName)) {
                                violations.push({
                                    context: mapping,
                                    message: () => sanitizeAndFormatString(stringResources.ColumnMapping_AlternateKeyDuplicated_Validation_ErrorMessage, `${fieldName}/${alternateKeyName}`)
                                });
                            }
                        } else {
                            fieldNames.add(fieldName);
                            alternateKeyNames.add(alternateKeyName);
                        }
                    }
                }
            });
        }

        return violations;
    }

    public static validateOutputColumnNames(transformation: BaseColumnMappingTransformation, value?: DataFlowColumn, violation?: IViolation, context?: IValidationContext): IViolation[] | undefined {
        return transformation.validateOutputColumnNames(context);
    }

    public addRuleBasedMapColumn(): DataFlowExpressionBasedMapColumn {
        const column = Owned.create<DataFlowExpressionBasedMapColumn>(DataFlowExpressionBasedMapColumn.MODEL, this);
        column.writeColumn = true;
        this.schema.push(column);
        return column;
    }

    public updateColumnReferences(): void {
        ColumnMappingTransformationUtils.updateColumnMapping(this);
    }

    public updateColumnInformation(updateCache: boolean = true): void {
        this._updateOutputColumns(updateCache);
    }

    public getMappingResults(mapping: IMapColumn): IMappingResult[] {
        return this._currentMappingResults.filter((mappingResult) => mappingResult.mapping === mapping);
    }

    public getMappingOutputColumns(mapping: IMapColumn): ColumnReference[] {
        return this._currentMappingResults.filter((mappingResult) => mappingResult.mapping === mapping)
            .map((mappingResult) => mappingResult.outputColumn);
    }

    public getAvailableArgumentsForTemplateCondition(): IDataFlowArguments {
        return PatternExpressionColumnsUtils.getAvailableArgumentsForTemplateCondition(this.dataflow.parameters, this.dataflow.cacheSinks, this);
    }

    public getAvailableArgumentsForColumnNameExpression(regexString?: string): IDataFlowArguments {
        return PatternExpressionColumnsUtils.getAvailableArgumentsForColumnNameExpression(this.dataflow.parameters, this.dataflow.cacheSinks, this, false, false, regexString);
    }

    public updateMappings(): void {
        this._updateOutputColumns();
        this._updateMappings.next();
    }

    public updateDownstreamTransformationMappings(): void {
        this.getAllDownTransformations().forEach((transformation) => {
            if (transformation instanceof BaseColumnMappingTransformation) {
                transformation.updateMappings();
            }
        });
    }

    public getExpressions(): DataFlowExpression[] {
        const expressions: DataFlowExpression[] = [];
        const transformationModel = Metamodel.modelOf(this);
        transformationModel.attributes.forEach((attribute) => {
            const value = attribute.value(this);
            if (value instanceof DataFlowExpression) {
                expressions.push(value);
            }
            if (Array.isArray(value) && value[0] instanceof DataFlowExpression) {
                expressions.push(...value);
            }
            if (Array.isArray(value) && (value[0] instanceof DataFlowMapColumn || value[0] instanceof DataFlowExpressionBasedMapColumn)) {
                value.filter((column) => column instanceof DataFlowExpressionBasedMapColumn).forEach((expBasedCol: DataFlowExpressionBasedMapColumn) => {
                    if (expBasedCol.dataFlowExpression) {
                        expressions.push(expBasedCol.dataFlowExpression);
                    }
                    if (expBasedCol.templateCondition) {
                        expressions.push(expBasedCol.templateCondition);
                    }
                });
            }
        });
        return expressions.filter((exp) => !!exp.templateExpression || exp.hasUserDefinedFunction);
    }

    public getUnusedInputColumns(): ColumnReference[] {
        return this._unusedInputColumns;
    }

    public initializeTransformation(contextData: IResourceContext): void {
        // If transformation is invalid then add schema to it.
        if (!this.schema.length && !!this.validateOutputColumnNamesAreUnique({ contextData })) {
            if (ColumnMappingTransformationUtils.shouldDefaultToRuleBasedMapping(this)) {
                this.schema.push(ColumnMappingTransformationUtils.createMatchAllColumnsRuleBasedMapping(this));
            } else {
                this.distinctInputColumnRefs.forEach((columnRef) => {
                    this.schema.push(DataFlowMapColumn.createFromColumnRef(columnRef, true));
                });
            }
            this._updateOutputColumns();
        }
    }

    public preRemoveAction(): void {
        this.schema.forEach((mapping) => {
            if (mapping instanceof DataFlowMapColumn && mapping.outputAlias !== mapping.columnRef.originalName) {
                mapping.outputAlias = mapping.columnRef.originalName;
                mapping.name = mapping.columnRef.originalName;
            }
        });
        this.updateColumnInformation(false);
    }

    protected _outputColumns(): DataFlowColumn[] {
        return this.columns;
    }

    protected _updateOutputColumns(updateCache: boolean = true): void {
        this._renamedColumns = [];
        const oldMappingResults = this._currentMappingResults;
        const newMappingResults: IMappingResult[] = [];
        if (!this.schema.length && this.populateMappingsOnEmptySchema) {
            // For auto-map, skipDuplicateMapInputs and skipDuplicateMapOutputs do the same thing
            const skipDuplicates = this.skipDuplicateMapInputs.expression === 'true' || this.skipDuplicateMapOutputs.expression === 'true';
            const columnsOnReset = this.inputColumnsOnReset;
            // remove duplicates by checking if findIndex gives the location of the column in input array
            const inputColumnsToOutput = columnsOnReset.filter((inputColumnRef, index) => !skipDuplicates
                || columnsOnReset.findIndex((columnRef) => columnRef.column.name === inputColumnRef.column.name) === index);
            newMappingResults.push(...inputColumnsToOutput.map((inputColumnRef) => this._createOutputMappingResult(oldMappingResults, inputColumnRef, inputColumnRef.originalName)));
        } else {
            const evaluatedMappings = this._evaluateMappings(this.schema.filter((mapping) => !!mapping.writeColumn));

            evaluatedMappings.forEach((mappingResult) => {
                const isDuplicateInput = this.skipDuplicateMapInputs.expression === 'true'
                    && newMappingResults.some((newMappingResult) => newMappingResult.baseEntity.name === mappingResult.baseEntity.name);
                const isDuplicateOutput = this.skipDuplicateMapOutputs.expression === 'true'
                    && newMappingResults.some((newMappingResult) => newMappingResult.outputColumn.name === mappingResult.outputColumn.name);
                const shouldSkip = isDuplicateInput || isDuplicateOutput;
                if (!shouldSkip) {
                    const newOutputMappingResult = this._createOutputMappingResult(oldMappingResults, mappingResult.baseEntity, mappingResult.outputColumn.name,
                        mappingResult.outputColumn, mappingResult.mapping);
                    newMappingResults.push(newOutputMappingResult);
                }
            });

            if (this._renamedColumns.length) {
                this.columnsRenamed(this._renamedColumns, updateCache);
            }
        }

        this._currentMappingResults = newMappingResults;
        this._unusedInputColumns = this._calculateUnusedInputColumns();
    }

    protected validateOutputColumnNames(context: IValidationContext): IViolation[] | undefined {
        const violations: IViolation[] = [];
        if (this.schema.length) {
            this.schema.forEach((schemaColumn) => {
                if (schemaColumn instanceof DataFlowMapColumn && !schemaColumn.outputAlias) {
                    violations.push({
                        context: this,
                        message: () => sanitizeAndFormatString(stringResources.DataFlowSchemaEmptyColumnNameError, this.name)
                    });
                }
            });
        }

        const uniqueViolations = this.validateOutputColumnNamesAreUnique(context);
        violations.push(...uniqueViolations);

        return violations;
    }

    protected validateOutputColumnNamesAreUnique(context: IValidationContext): IViolation[] {
        const uniqueViolations = UniqueCaseSensitiveValidator.INSTANCE.validate({}, BaseColumnMappingTransformation.MODEL.attribute('outputColumns'), this.outputColumns(), this, context);
        return uniqueViolations || [];
    }

    protected updateInputTransformationCacheInternal(): void {
        const generalArgs = generateGrammarForColumns(this.distinctInputColumnRefs, this.dataflow.cacheSinks, this.dataflow.parameters);
        this.schema.forEach((mapping) => {
            if (mapping instanceof DataFlowExpressionBasedMapColumn) {
                if (GlobalVariables.validateDataFlowAPI) {
                    asyncUpdateIdentifiers(generalArgs, mapping.hierarchyLevelExpression, this);
                } else {
                    updateIdentifiers(generalArgs, mapping.hierarchyLevelExpression);
                }
            }
        });

        this._updateOutputColumns();

        const expressionBasedColumns = this.schema.filter((mapping) => mapping instanceof ExpressionBasedColumnBase) as unknown as ExpressionBasedColumnBase[];
        expressionBasedColumns.forEach((mapping) => {
            const regexString = mapping instanceof DataFlowExpressionBasedMapColumn ? mapping.regexString : '';
            this._updateExpressionColumnIdentifiers([mapping], this.getAvailableArgumentsForColumnNameExpression(regexString), this.getAvailableArgumentsForTemplateCondition());
        });

        this._updateMappings.next();

        super.updateInputTransformationCacheInternal();
    }

    protected getOutputColumnDataType(baseEntity: IBasedOnEntityRef): IDataFlowDataType {
        return ComplexTypeUtils.getFieldDataType(baseEntity.entity, baseEntity.name);
    }

    protected insertBetweenInternal(sourceTransformation: Transformation, targetTransformation: Transformation): void {
        const downStreamTransformations = [targetTransformation];
        downStreamTransformations.push(...targetTransformation.getAllDownTransformations());
        downStreamTransformations.forEach((downStreamTransformation) => this.updateExpressions(downStreamTransformation, sourceTransformation));
    }

    protected updateExpressions(downStreamTransformation: Transformation, sourceTransformation: Transformation): void {
        this._updateOutputColumns();
        const distinctColumnRefs = downStreamTransformation.distinctInputColumnRefs;
        downStreamTransformation.getExpressions().forEach((dataFlowExpression: DataFlowExpression) => {
            let positionShift = 0;
            // Need to sort the expressions by start index, so our position shift is always incremental and global for the columns that still need rename.
            const expressions = getSortedExpressionsInColumnArguments(dataFlowExpression.argumentsUsed.dataFlowColumns);

            expressions.forEach((usedExpression) => {
                usedExpression.expression.start += positionShift;
                usedExpression.expression.stop += positionShift;

                if (usedExpression.column.column.dataFlowColumn.ownerTransformation === sourceTransformation) {
                    const columnRef = distinctColumnRefs.find((cRef) => cRef.column === usedExpression.column.column.dataFlowColumn);
                    if (columnRef && columnRef.isAmbiguous) {
                        const columnNameInSelect = this._currentMappingResults.find((mappingResult) => mappingResult.baseEntity.entity === columnRef.column).outputColumn.column.name;
                        const newArgument = `${this.name}@${columnNameInSelect}`;
                        positionShift += newArgument.length - (usedExpression.expression.stop - usedExpression.expression.start + 1);
                        this._updateExpressionArgument(dataFlowExpression, usedExpression.expression, newArgument);
                    }
                }
            });
        });
    }

    private _calculateUnusedInputColumns(): ColumnReference[] {
        const usedInputColumns = new Set<ColumnReference>();
        this._currentMappingResults.forEach((mappingResult) => {
            const inputColumnRef = this.distinctInputColumnRefs.find((columnRef) => columnRef.column === mappingResult.baseEntity.rootEntity);
            if (inputColumnRef) {
                usedInputColumns.add(inputColumnRef);
            }
        });
        return this.distinctInputColumnRefs.filter((inputColumn) => !usedInputColumns.has(inputColumn));
    }

    private _evaluateMappings(mappings: IMapColumn[]): IMappingResult[] {
        const evaluatedMappings = new Array<IMappingResult>();
        for (const mapping of mappings) {
            if (mapping instanceof DataFlowMapColumn && mapping.columnRef) {
                // This is to determine if the input column for this mapping still exists, otherwise we should not output it, check root column for field selection scenario
                const existingColumn = this.distinctInputColumnRefs.find((column) => column.name === mapping.columnRef.name || column.name === mapping.columnRef.rootColumnName);
                if (existingColumn) {
                    const outputColumn = Owned.create<DataFlowColumn>(Metamodel.modelFor(DataFlowColumn), this);
                    outputColumn.name = mapping.outputAlias;
                    outputColumn.dataFlowColumnType = this.getOutputColumnDataType(existingColumn);
                    evaluatedMappings.push({ mapping, baseEntity: mapping.columnRef, outputColumn: new ColumnReference(outputColumn) });
                }
            } else if (mapping instanceof DataFlowExpressionBasedMapColumn) {
                const mappingOutputColumns = ColumnExpressionProcessor.processNewColumns([mapping], this.distinctInputColumnRefs, this);
                evaluatedMappings.push(...mappingOutputColumns.map((outputColumn) => ({ mapping, baseEntity: outputColumn.baseEntity, outputColumn: new ColumnReference(outputColumn.column) })));
            }
        }
        return evaluatedMappings;
    }

    private _getUpdatedColumnLength(): number {
        return this.outputColumns().filter((outputColumn) => this._isColumnUpdate(outputColumn)).length;
    }

    private _getDroppedColumnLength(): number {
        return this.getUnusedInputColumns().length;
    }

    private _getUnchangedColumnLength(): number {
        return this.outputColumns().length - this._getUpdatedColumnLength();
    }

    private _getColumnUpdateIcon(column: DataFlowColumn): string {
        return this._isColumnUpdate(column) ? 'svg-i-9x9-Update' : '';
    }

    private _isColumnUpdate(column: DataFlowColumn): boolean {
        const mappingResult = this._currentMappingResults.find((mappingResult) => mappingResult.outputColumn.column === column);
        return mappingResult && mappingResult.baseEntity.entity.name !== column.name;
    }

    private _getInputColumnName(column: DataFlowColumn): string {
        const mappingResult = this._currentMappingResults.find((mappingResult) => mappingResult.outputColumn.column === column);
        return mappingResult ? mappingResult.baseEntity.name : column.name;
    }

    private _createOutputMappingResult(oldMappingResults: IMappingResult[], baseEntity: IBasedOnEntityRef, aliasNameForNewColumn: string,
        alreadyConstructedOutputColumn?: ColumnReference, originatingMapping?: IMapColumn): IMappingResult {
        const cachedMappingResultIndex = oldMappingResults.findIndex((oldMappingResult) => oldMappingResult.baseEntity.entity === baseEntity.entity
            && oldMappingResult.baseEntity.name === baseEntity.name && (!oldMappingResult.mapping || oldMappingResult.mapping === originatingMapping));
        const newMappingResult = cachedMappingResultIndex >= 0
            ? oldMappingResults[cachedMappingResultIndex]
            : {
                baseEntity,
                mapping: originatingMapping,
                outputColumn: alreadyConstructedOutputColumn || new ColumnReference(Owned.create<DataFlowColumn>(DataFlowColumn.MODEL, this))
            };

        newMappingResult.outputColumn.column.dataFlowColumnType = this.getOutputColumnDataType(baseEntity);
        if (newMappingResult.outputColumn.column.name !== aliasNameForNewColumn) {
            newMappingResult.outputColumn.column.name = aliasNameForNewColumn;
            if (cachedMappingResultIndex >= 0) {
                this._renamedColumns.push(newMappingResult.outputColumn.column);
            }
        }

        if (cachedMappingResultIndex >= 0) {
            oldMappingResults.splice(cachedMappingResultIndex, 1);
        }

        return newMappingResult;
    }

    private static validateSkipDuplicateMapInputs(owner: BaseColumnMappingTransformation, value: DataFlowExpression): IViolation[] {
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.SkipDuplicateMappingInputs);
    }

    private static validateSkipDuplicateMapOutputs(owner: BaseColumnMappingTransformation, value: DataFlowExpression): IViolation[] {
        return Transformation.validateTemplateExpressions(owner, [value], stringResources.SkipDuplicateMappingOutputs);
    }

}
